import React from 'react';
import clsx from 'clsx';
import { DropdownField } from '@/components/dropdowns/DropdownField/DropdownField';
import { ListItemDropdown } from '@/components/list-item/ListItemDropdown/ListItemDropdown';
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useLayerNodeState } from '@/hooks/useLayerNodeState';
import { useLayer } from '@craftjs/layers';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import { setIsOpenSaveSection } from '@/store/section/slice';
import { useAppDispatch } from '@/store/hooks';
import { canDuplicateNode, isContainerNode, isPopupNode } from '@/utils/helperFunc';
import { canDeleteNode } from '@/utils/canDeleteNode';
import type { Breakpoint } from '@builder4/shared/types/layout';

interface LayerDropdownProps {
  className: string;
  onClick?: () => void;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  childrenTrigger: React.ReactNode;
  onEnterEditTitleMode: () => void;
}

export const LayerDropdown: React.FC<LayerDropdownProps> = ({
  className = '',
  onClick,
  setDropdownOpen,
  childrenTrigger,
  dropdownOpen,
  onEnterEditTitleMode,
}) => {
  let id: string | undefined;
  let isInsideCraft = true;

  try {
    const layer = useLayer((layer) => ({ id: layer.id }));
    id = layer.id;
  } catch {
    isInsideCraft = false;
  }

  const { isHidden: isLayerHidden, isLocked: isLayerLocked } = useLayerNodeState(id);

  const dispatch = useAppDispatch();

  const { removeNode, duplicateNode, toggleLock, toggleVisibility, copyNode, pasteNode } =
    useCraftHelper();
  const { reAdustFillableObjectSpace } = useContainerHelper();

  const { isRootContainer, isPopup, isDeletable, canDuplicate, parentId } = useEditor(
    (_, query) => {
      // Cautiously guard against undefined id or node
      const defaultValues = {
        isRootContainer: false,
        isPopup: false,
        isDeletable: false,
        canDuplicate: false,
        parentId: '',
      };
      if (!id) return defaultValues;
      const node = query.node(id).get();
      if (!node) return defaultValues;
      return {
        isRootContainer: isContainerNode(node) && node.data.parent === ROOT_NODE,
        isPopup: isPopupNode(node),
        isDeletable: canDeleteNode(query, id),
        canDuplicate: canDuplicateNode(node),
        parentId: node.data.parent as string,
      };
    },
  );

  const handleClick = (event: React.MouseEvent) => {
    if (onClick) onClick();
    event.stopPropagation();
  };

  const handleToggleNodeVisibility = () => {
    if (!isInsideCraft || !id) {
      console.warn('Visibility toggle is disabled outside Craft or without a valid node ID.');
      return;
    }
    toggleVisibility(id);
    setDropdownOpen(false);
  };

  const handleToggleNodeLock = () => {
    if (!isInsideCraft || !id) {
      console.warn('Lock toggle is disabled outside Craft or without a valid node ID.');
      return;
    }
    toggleLock(id);
    setDropdownOpen(false);
  };

  const handleDeleteNode = () => {
    if (!isInsideCraft || !id) {
      console.warn('Delete action is disabled outside Craft or without a valid node ID.');
      return;
    }
    removeNode(id);
    setDropdownOpen(false);
  };

  const handleDuplicate = () => {
    if (!isInsideCraft || !id) {
      console.warn('Duplicate action is disabled outside Craft or without a valid node ID.');
      return;
    }
    const newNodeId = duplicateNode(id);
    if (!newNodeId) return;
    ['desktop', 'tablet', 'mobile'].forEach((bp) => {
      reAdustFillableObjectSpace(parentId as string, newNodeId, bp as Breakpoint);
    });

    setDropdownOpen(false);
  };

  const handlePasteNode = () => {
    if (!isInsideCraft || !id) {
      console.warn('Paste action is disabled outside Craft or without a valid node ID.');
      return;
    }
    pasteNode(id);
    setDropdownOpen(false);
  };

  const handleTitleEdit = () => {
    setDropdownOpen(false);
    setTimeout(() => {
      // Delay is important to avoid aria-hidden error
      onEnterEditTitleMode();
    }, 250);
  };

  const handleCopyNode = () => {
    if (!id) {
      console.warn('Copy action is disabled without a valid node ID.');
      return;
    }
    setDropdownOpen(false);
    setTimeout(() => {
      copyNode(id);
    }, 200);
  };

  const handleOpenSaveSection = () => {
    dispatch(setIsOpenSaveSection(true));
    setDropdownOpen(false);
  };

  return (
    <div onClick={handleClick} className={clsx('', className)}>
      <DropdownField
        open={dropdownOpen}
        onOpenChange={(open: boolean) => setDropdownOpen(open)}
        childrenMenu={
          <>
            <div className="p-1">
              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={handleTitleEdit}
                href=""
                shortcut="⌘R"
                text="Rename"
                selection={false}
                allowSelectionToggle={false}
              />
              {canDuplicate && (
                <>
                  <ListItemDropdown
                    as="div"
                    className="w-full cursor-pointer"
                    handlerClick={handleDuplicate}
                    href="#"
                    shortcut="⌘+D"
                    text="Duplicate"
                    selection={false}
                    allowSelectionToggle={false}
                  />
                  <ListItemDropdown
                    as="div"
                    className="w-full cursor-pointer"
                    handlerClick={handleCopyNode}
                    href=""
                    shortcut="⌘+C"
                    text="Copy"
                    selection={false}
                    allowSelectionToggle={false}
                  />
                </>
              )}
              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={handlePasteNode}
                href="#"
                shortcut="⌘+V"
                text="Paste"
                selection={false}
                allowSelectionToggle={false}
              />
              {(isRootContainer || isPopup) && (
                <ListItemDropdown
                  as="div"
                  className="w-full cursor-pointer"
                  handlerClick={handleOpenSaveSection}
                  href="#"
                  shortcut="⌘+S"
                  text="Save section"
                  selection={false}
                  allowSelectionToggle={false}
                />
              )}
            </div>
            <div className="h-[1px] w-full bg-neutral-100"></div>
            <div className="p-1">
              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={handleToggleNodeVisibility}
                href="#"
                shortcut="⌘+H"
                text={isLayerHidden ? 'Show' : 'Hide'}
                selection={false}
                allowSelectionToggle={false}
              />
              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={handleToggleNodeLock}
                href="#"
                shortcut="⌘+L"
                text={isLayerLocked ? 'Unlock' : 'Lock'}
                selection={false}
                allowSelectionToggle={false}
              />
              {isDeletable && (
                <ListItemDropdown
                  as="div"
                  className="w-full cursor-pointer"
                  handlerClick={handleDeleteNode}
                  href="#"
                  shortcut="⌫"
                  text="Delete"
                  selection={false}
                  allowSelectionToggle={false}
                />
              )}
            </div>
          </>
        }
        childrenTrigger={
          <DropdownMenuTrigger className="cursor-pointer outline-none">
            {childrenTrigger}
          </DropdownMenuTrigger>
        }
        className="w-full max-w-[216px]"
        classNameContent="p-0 min-w-[160px]"
        size="small"
        text=""
        icon={null}
      />
    </div>
  );
};
